/**
 * Firebase Cloud Messaging — helpers de frontend.
 * Gestiona el permiso de notificaciones, obtención del FCM token y
 * sincronización con el backend.
 *
 * Variables de entorno necesarias:
 *   VUE_APP_FCM_VAPID_KEY  — Clave pública ECDH (Firebase Console → Cloud Messaging → Web Push)
 */
import { getApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, deleteToken } from 'firebase/messaging';

const VAPID_KEY = process.env.VUE_APP_FCM_VAPID_KEY || '';
const LS_DISMISSED = 'pushOptInDismissed';

// ─────────────────────────────────────────────────
// Estado público del permiso
// ─────────────────────────────────────────────────

export function getPushPermissionState() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

export function isPushDismissed() {
  return localStorage.getItem(LS_DISMISSED) === '1';
}

export function dismissPushOptIn() {
  localStorage.setItem(LS_DISMISSED, '1');
}

export function isFcmEnabled() {
  return Boolean(VAPID_KEY);
}

// ─────────────────────────────────────────────────
// Service worker de FCM
// El handler FCM está integrado en service-worker.js para evitar conflictos
// de scope (/). Simplemente esperamos a que el SW principal esté activo.
// ─────────────────────────────────────────────────

async function getFcmServiceWorkerRegistration() {
  if (!('serviceWorker' in navigator)) return undefined;
  try {
    // navigator.serviceWorker.ready resuelve cuando hay un SW activo
    // controlando la página (el service-worker.js que ya incluye FCM)
    return await navigator.serviceWorker.ready;
  } catch (err) {
    console.warn('[FCM] Error obteniendo SW activo:', err);
    return undefined;
  }
}

// ─────────────────────────────────────────────────
// Solicitar permiso y obtener token
// ─────────────────────────────────────────────────

/**
 * Solicita permiso de notificaciones al usuario, obtiene el FCM token
 * y lo guarda en el backend.
 * @returns {'granted'|'denied'|'unsupported'|'error'}
 */
export async function requestAndSavePushToken() {
  if (!isFcmEnabled()) {
    console.warn('[FCM] VAPID key no configurada (VUE_APP_FCM_VAPID_KEY).');
    return 'error';
  }
  if (!('Notification' in window)) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission;

  try {
    const app = getApp();
    const messaging = getMessaging(app);
    const swReg = await getFcmServiceWorkerRegistration();

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });

    if (token) await saveFcmTokenToBackend(token);
    return 'granted';
  } catch (err) {
    console.error('[FCM] Error obteniendo token:', err);
    return 'error';
  }
}

// ─────────────────────────────────────────────────
// Guardar / borrar token en backend
// ─────────────────────────────────────────────────

async function getIdToken() {
  const { getAuth } = await import('firebase/auth');
  const user = getAuth().currentUser;
  if (!user) return null;
  return user.getIdToken();
}

export async function saveFcmTokenToBackend(token) {
  const idToken = await getIdToken();
  if (!idToken) return;
  await fetch('/api/users/fcm-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ token }),
  }).catch((err) => console.warn('[FCM] No se pudo guardar token:', err));
}

/** Llama al logout o cuando el usuario desactiva las notificaciones. */
export async function removeFcmTokenFromBackend() {
  try {
    const app = getApp();
    const messaging = getMessaging(app);
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    await deleteToken(messaging).catch(() => { /* token ya caducado, no crítico */ });
  // eslint-disable-next-line no-empty
  } catch (_) { /* no crítico si FCM no está inicializado */ }

  const idToken = await getIdToken();
  if (!idToken) return;
  await fetch('/api/users/fcm-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ token: null }),
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  }).catch(() => { /* no crítico en logout */ });
}

// ─────────────────────────────────────────────────
// Mensajes en foreground (app abierta)
// ─────────────────────────────────────────────────

const noopUnsubscribe = () => { /* no-op unsubscribe */ };

/**
 * Escucha mensajes push cuando la app está en primer plano.
 * @param {function} onNotification - callback con el payload FCM
 * @returns {function} unsubscribe
 */
export function setupForegroundMessages(onNotification) {
  if (!isFcmEnabled()) return noopUnsubscribe;
  try {
    const app = getApp();
    const messaging = getMessaging(app);
    return onMessage(messaging, (payload) => {
      onNotification?.(payload);
    });
  } catch (err) {
    console.warn('[FCM] No se pudo configurar foreground messages:', err);
    return noopUnsubscribe;
  }
}
